import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, errorMessage } from "../../api/client";
import { useFields } from "../../api/hooks";
import type { FieldDef } from "../../api/types";
import { Chip, DATA_TYPES, Kpi, PageHead, Segmented, Spinner, useToast, type Tone } from "../../components/ui";
import { useAuth } from "../../lib/auth";

const ORIGIN: Record<string, [string, Tone]> = { core: ["Socle", "gray"], source: ["Import", "blue"], custom: ["Personnalisé", "purple"], mission: ["Mission", "orange"] };
const STORAGE: Record<string, string> = { column: "Colonne native", attrs: "Donnée dynamique", contact_point: "Coordonnée" };
const STATUS: Record<string, [string, Tone]> = { active: ["Actif", "green"], proposed: ["Proposé", "orange"], archived: ["Archivé", "gray"] };

export function FieldsPage() {
  const me = useAuth();
  const [scope, setScope] = useState<"all" | "company" | "contact" | "added">("all");
  const fields = useFields(true);
  const qc = useQueryClient();
  const toast = useToast();
  const can = me.can("fields.manage");
  const list = useMemo(() => (fields.data ?? []).filter((f) =>
    scope === "all" ? f.status !== "archived" : scope === "added" ? f.origin !== "core" : f.entity === scope && f.status !== "archived"), [fields.data, scope]);
  const d = fields.data ?? [];

  async function patch(f: FieldDef, body: Partial<FieldDef>) {
    try {
      await api(`/fields/${f.id}`, { method: "PATCH", json: body });
      qc.invalidateQueries({ queryKey: ["fields"] });
      toast(`« ${f.label} » mis à jour`);
    } catch (e) {
      toast(errorMessage(e), true);
    }
  }

  return (
    <main className="page">
      <PageHead eyebrow="Master Data" title="Registre des champs"
        right={<Segmented label="Filtre" value={scope} onChange={setScope} options={[["all", "Tous"], ["company", "Entreprise"], ["contact", "Contact"], ["added", "Ajoutés par les imports"]]} />} />
      <div className="kpis">
        <Kpi label="Champs actifs" value={d.filter((f) => f.status === "active").length} />
        <Kpi label="Ajoutés par les imports" value={d.filter((f) => f.origin === "source" && f.status !== "archived").length} tone="info" sub="aucune colonne perdue" />
        <Kpi label="Filtrables" value={d.filter((f) => f.is_filterable && f.status === "active").length} />
        <Kpi label="Archivés" value={d.filter((f) => f.status === "archived").length} sub="colonnes vides ou retirées" />
      </div>
      <div className="tile flush">
        {fields.isLoading ? <div className="empty"><Spinner /></div> : (
          <div className="scroll">
            <table className="table">
              <thead><tr><th>Champ</th><th>Entité</th><th>Type</th><th>Origine</th><th>Stockage</th><th>Statut</th><th>Filtrable</th><th>Exportable</th></tr></thead>
              <tbody>
                {list.map((f) => (
                  <tr key={f.id}>
                    <td><b>{f.label}</b><div className="mono faint">{f.key}{f.related_field_key ? ` · proche de ${f.related_field_key}` : ""}</div></td>
                    <td>{f.entity === "company" ? "Entreprise" : "Contact"}</td>
                    <td>{DATA_TYPES[f.data_type] ?? f.data_type}</td>
                    <td><Chip tone={ORIGIN[f.origin][1]}>{ORIGIN[f.origin][0]}</Chip>{f.created_from_import_id && <span className="faint"> #{f.created_from_import_id}</span>}</td>
                    <td className="muted">{f.derived ? "Calculé" : STORAGE[f.storage]}</td>
                    <td>
                      {can && f.origin !== "core" ? (
                        <select className="select sm" aria-label={`Statut de ${f.label}`} value={f.status} onChange={(e) => patch(f, { status: e.target.value as FieldDef["status"] })}>
                          <option value="active">Actif</option><option value="proposed">Proposé</option><option value="archived">Archivé</option>
                        </select>
                      ) : <Chip tone={STATUS[f.status][1]} dot>{STATUS[f.status][0]}</Chip>}
                    </td>
                    <td><input type="checkbox" className="switch" aria-label={`${f.label} filtrable`} checked={f.is_filterable} disabled={!can} onChange={(e) => patch(f, { is_filterable: e.target.checked })} /></td>
                    <td><input type="checkbox" className="switch" aria-label={`${f.label} exportable`} checked={f.is_exportable} disabled={!can} onChange={(e) => patch(f, { is_exportable: e.target.checked })} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </main>
  );
}
